import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { requireData, TradingEngineError } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { useAccounts } from "../settings/queries.js";

type Action = components["schemas"]["ManualOrderRequest"]["action"];
type Preview = components["schemas"]["ManualOrderPreview"];
type ManualOrder = components["schemas"]["ManualOrder"];

const labels: Record<Action, string> = {
  "buy-to-open": "Buy to open long",
  "sell-to-close": "Sell to close long",
  "sell-to-open": "Sell to open short",
  "buy-to-close": "Buy to close short",
};

function errorText(value: unknown): string {
  if (value instanceof TradingEngineError) {
    const details = value.details as { brokerRejection?: { code?: unknown } } | undefined;
    const code = details?.brokerRejection?.code;
    return typeof code === "string" && /^[a-z][a-z0-9_-]{0,63}$/iu.test(code)
      ? `${value.message} (${code})`
      : value.message;
  }
  return value instanceof Error
    ? value.message
    : "Request failed. No order should be retried until its status is checked.";
}

export function ManualOrderPanel({ symbol }: { readonly symbol: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const accounts = useAccounts();
  const status = useQuery({
    queryKey: ["manual-trading-status"],
    queryFn: async ({ signal }) => requireData(await api.GET("/manual-trading/status", { signal })),
  });
  const [accountId, setAccountId] = useState("");
  const [action, setAction] = useState<Action>("buy-to-open");
  const [quantity, setQuantity] = useState("1");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const previewMutation = useMutation({
    mutationFn: async () =>
      requireData(
        await api.POST("/manual-orders/preview", {
          body: { accountId, symbol, action, quantity: Number(quantity) },
        }),
      ),
    onSuccess: (result) => {
      setPreview(result);
      setConfirmed(false);
      setOrderId(null);
    },
  });
  const submitMutation = useMutation({
    mutationFn: async (value: Preview): Promise<ManualOrder> =>
      requireData(
        await api.POST("/manual-orders/{previewId}/submit", {
          params: { path: { previewId: value.id } },
        }),
      ),
    onSuccess: async (result) => {
      setOrderId(result.id);
      setPreview(null);
      setConfirmed(false);
      await queryClient.invalidateQueries();
    },
  });
  const order = useQuery({
    queryKey: ["manual-order", orderId],
    enabled: orderId !== null,
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/manual-orders/{orderId}", {
          params: { path: { orderId: orderId ?? "" } },
          signal,
        }),
      ),
    refetchInterval: (query) =>
      query.state.data !== undefined &&
      ["filled", "cancelled", "rejected", "not-submitted"].includes(query.state.data.status)
        ? false
        : 5_000,
  });
  const readyAccounts = (accounts.data ?? []).filter(
    (item) => item.status === "ready" && item.capabilities.includes("stocks"),
  );
  const selectedAccount = readyAccounts.find((item) => item.id === accountId);
  const changed = () => {
    setPreview(null);
    setConfirmed(false);
  };
  const onPreview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPreview(null);
    if (accountId !== "" && Number.isInteger(Number(quantity))) previewMutation.mutate();
  };

  return (
    <section
      className="content-card section-stack manual-order-panel"
      aria-label={`Manual ${symbol} order`}
    >
      <div className="connection-heading">
        <div>
          <h2>Manual stock order</h2>
          <p className="section-description">
            Separate from Bots. Whole-share market orders during regular hours only.
          </p>
        </div>
      </div>
      {status.isLoading ? <p role="status">Checking manual trading availability…</p> : null}
      {status.error !== null ? (
        <p role="alert">Could not verify manual trading availability. Ordering is disabled.</p>
      ) : null}
      {status.data?.enabled === false ? (
        <p role="status">Manual trading is disabled for this environment.</p>
      ) : null}
      {status.data?.enabled === true ? (
        <>
          <form className="manual-order-form" onSubmit={onPreview}>
            <div className="field-stack">
              <label htmlFor="manual-account">Tastytrade account</label>
              <select
                id="manual-account"
                required
                value={accountId}
                onChange={(event) => {
                  setAccountId(event.currentTarget.value);
                  changed();
                }}
              >
                <option value="">Choose an account</option>
                {readyAccounts.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.displayName} · {item.providerAccountId}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="manual-action">Action</label>
              <select
                id="manual-action"
                value={action}
                onChange={(event) => {
                  setAction(event.currentTarget.value as Action);
                  changed();
                }}
              >
                {(Object.keys(labels) as Action[]).map((value) => (
                  <option
                    key={value}
                    disabled={
                      value === "sell-to-open" &&
                      selectedAccount !== undefined &&
                      !selectedAccount.capabilities.includes("short-selling")
                    }
                    value={value}
                  >
                    {labels[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="manual-quantity">Shares</label>
              <input
                id="manual-quantity"
                min="1"
                max="1000"
                step="1"
                required
                type="number"
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.currentTarget.value);
                  changed();
                }}
              />
            </div>
            <button
              className="button"
              disabled={
                previewMutation.isPending || submitMutation.isPending || readyAccounts.length === 0
              }
              type="submit"
            >
              {previewMutation.isPending ? "Checking broker…" : "Preview order"}
            </button>
          </form>
          {readyAccounts.length === 0 && !accounts.isLoading ? (
            <p role="status">No ready stock-trading account is connected.</p>
          ) : null}
          {previewMutation.error !== null ? (
            <p role="alert">{errorText(previewMutation.error)}</p>
          ) : null}
          {preview !== null ? (
            <div
              className="manual-order-confirmation"
              role="group"
              aria-label="Confirm manual order"
            >
              <h3>Confirm {labels[preview.action]}</h3>
              <p>
                {preview.quantity} {preview.symbol} shares ·{" "}
                {selectedAccount?.displayName ?? preview.accountId}
              </p>
              <p>
                Broker position: {preview.positionQuantity} shares. Indicative broker quote: $
                {preview.indicativePriceUsd}; estimated notional: ${preview.estimatedNotionalUsd}.
              </p>
              <p>
                Quote at {new Date(preview.quoteAt).toLocaleTimeString()}. Preview expires{" "}
                {new Date(preview.expiresAt).toLocaleTimeString()}.
              </p>
              <p>{preview.warning}</p>
              {preview.brokerWarnings.length > 0 ? (
                <div role="alert">
                  <strong>Tastytrade preflight warnings</strong>
                  <ul>
                    {preview.brokerWarnings.map((warning, index) => (
                      <li key={`${warning.code ?? "warning"}-${index}`}>
                        {warning.message}
                        {warning.code === undefined ? "" : ` (${warning.code})`}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <label className="manual-order-confirmation__check">
                <input
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.currentTarget.checked)}
                  type="checkbox"
                />{" "}
                I confirm this live Tastytrade market order
                {preview.brokerWarnings.length > 0 ? " and have reviewed the broker warnings" : ""}.
              </label>
              <button
                className="button"
                disabled={!confirmed || submitMutation.isPending}
                onClick={() => {
                  if (Date.parse(preview.expiresAt) <= Date.now()) {
                    setPreview(null);
                    return;
                  }
                  submitMutation.mutate(preview);
                }}
                type="button"
              >
                {submitMutation.isPending ? "Submitting once…" : "Submit live order"}
              </button>
            </div>
          ) : null}
          {submitMutation.error !== null ? (
            <p role="alert">
              {errorText(submitMutation.error)} If submission is uncertain, check History and
              Tastytrade before previewing again.
            </p>
          ) : null}
          {orderId !== null ? (
            <div role="status">
              <strong>Order {orderId}</strong>: {order.data?.status ?? "Checking broker status…"}
              {order.data?.status === "unknown" ? (
                <p>
                  Broker outcome is uncertain. Do not submit another order for this account and
                  symbol.
                </p>
              ) : null}
              {order.data?.status === "not-submitted" ? (
                <p>Tastytrade preflight failed. No live order was sent.</p>
              ) : null}
              {order.error !== null ? (
                <p>
                  Broker status could not be refreshed. Do not resubmit until the order is
                  reconciled.
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
